import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { translateBackendMessage } from '../../utils/translateMessage';
import { changePassword } from '../../redux/slices/authSlice';
import styles from './Profile.module.css';
// import Navbar1 from '../../components/layout/Navbar';

const Profile = () => {
  const { user } = useSelector(state => state.auth);
  const mosadsList = useSelector(state => state.mosads.mosadsList) || [];
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password form (backend change-password has no strength rules; only required fields + confirm match)
  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: (values) => {
      const errors = {};
      if (!values.currentPassword) {
        errors.currentPassword = t('profile.validation.currentPasswordRequired');
      }
      if (!values.newPassword) {
        errors.newPassword = t('profile.validation.newPasswordRequired');
      }
      if (!values.confirmPassword) {
        errors.confirmPassword = t('profile.validation.confirmPasswordRequired');
      } else if (values.confirmPassword !== values.newPassword) {
        errors.confirmPassword = t('profile.validation.passwordMismatch');
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        setPasswordLoading(true);
        setPasswordError(null);
        
        const result = await dispatch(changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        })).unwrap();
        
        toast.success(t('profile.passwordUpdated'), {
          position: 'top-left',
          rtl: true
        });
        
        // Reset form
        passwordFormik.resetForm();
        
      } catch (error) {
        const errorMessage = error.message || error || t('errors.generic');
        const translated = translateBackendMessage(errorMessage);
        const displayMessage = translated || errorMessage;
        setPasswordError(displayMessage);
        toast.error(displayMessage, {
          position: 'top-left',
          rtl: true
        });
      } finally {
        setPasswordLoading(false);
      }
    },
  });

  // Get status badge color class
  const getStatusBadgeClass = (status) => {// U: Unverified, A: Active, P: Postpone, I: Invited
    switch (status) {
      case 'A':
        return 'bg-success';
      case 'I':
        return 'bg-info';
      case 'P':
        return 'bg-warning text-dark';
      case 'U':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  // Get status label (translated)
  const getStatusLabel = (status) => {
    const statusMap = {
      'A': t('status.active'),
      'I': t('status.invited'),
      'P': t('status.postpone'),
      'U': t('status.unverified'),
    };
    return statusMap[status] || status;
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (profileSuccess) {
      const timer = setTimeout(() => setProfileSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [profileSuccess]);

  return (
    <>
      {/*  <Navbar1 className={styles.mosadsNavbar} isMosadsScreen={true}/> */}

      <div className={`${styles.header} d-flex justify-content-between align-items-center mb-4`}>
        <h2 className="mb-0">{t('profile.title')}</h2>
      </div>

      <Row className="g-4">
        <Col xs={12} lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">{t('profile.personalInfo')}</h5>
            </Card.Header>
            <Card.Body>
              {profileSuccess && (
                <Alert variant="success" className="mb-3">
                  {t('messages.updated')}
                </Alert>
              )}
              {profileError && (
                <Alert variant="danger" className="mb-3" dismissible onClose={() => setProfileError(null)}>
                  {profileError}
                </Alert>
              )}
              <Form>
                {/* userId & username in the same row */}
                <Row className="mb-3">
                <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label className={styles.formLabel}>{t('auth.email')}</Form.Label>
                      <Form.Control 
                        type="email" 
                        value={user?.email || ''}
                        readOnly
                        disabled
                        className={`bg-light ${styles.formControl} ${styles.emailField}`}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label className={styles.formLabel}>{t('profile.username')}</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={user?.fullName || ''}
                        readOnly 
                        disabled
                        className={`bg-light ${styles.formControl}`}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label className={styles.formLabel}>{t('profile.numOfActiveMosads')}</Form.Label>
                      <div className="mt-2">
                        <span className={`badge ${getStatusBadgeClass()}`} style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', fontWeight: '500' }}>
                          {mosadsList.length}
                        </span>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                    <Form.Label className={styles.formLabel}>{t('auth.status')}</Form.Label>
                    <div>
                      <span 
                        className={`badge ${getStatusBadgeClass(user?.status)}`}
                        style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', fontWeight: '500' }}
                      >
                        {getStatusLabel(user?.status)}
                      </span>
                    </div>
                    </Form.Group>
                  </Col>
                </Row>
                
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">{t('profile.updatePassword')}</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={passwordFormik.handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className={styles.formLabel}>{t('profile.currentPassword')}</Form.Label>
                  <div className={styles.passwordInputWrapper}>
                    <Form.Control 
                      type={showCurrentPassword ? "text" : "password"} 
                      name="currentPassword"
                      value={passwordFormik.values.currentPassword}
                      onChange={passwordFormik.handleChange}
                      onBlur={passwordFormik.handleBlur}
                      isInvalid={passwordFormik.touched.currentPassword && passwordFormik.errors.currentPassword}
                      className={styles.formControl}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      tabIndex={-1}
                    >
                      {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {passwordFormik.errors.currentPassword}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className={styles.formLabel}>{t('profile.newPassword')}</Form.Label>
                  <div className={styles.passwordInputWrapper}>
                    <Form.Control 
                      type={showNewPassword ? "text" : "password"} 
                      name="newPassword"
                      value={passwordFormik.values.newPassword}
                      onChange={passwordFormik.handleChange}
                      onBlur={passwordFormik.handleBlur}
                      isInvalid={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
                      className={styles.formControl}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      tabIndex={-1}
                    >
                      {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <Form.Text className="text-muted d-block mt-2">
                    {t('profile.passwordRequirements')}
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {passwordFormik.errors.newPassword}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className={styles.formLabel}>{t('profile.confirmNewPassword')}</Form.Label>
                  <div className={styles.passwordInputWrapper}>
                    <Form.Control 
                      type={showConfirmPassword ? "text" : "password"} 
                      name="confirmPassword"
                      value={passwordFormik.values.confirmPassword}
                      onChange={passwordFormik.handleChange}
                      onBlur={passwordFormik.handleBlur}
                      isInvalid={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                      className={styles.formControl}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {passwordFormik.errors.confirmPassword}
                  </Form.Control.Feedback>
                </Form.Group>
                <div className="d-flex justify-content-end gap-2">
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => passwordFormik.resetForm()}
                    disabled={passwordLoading}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    type="submit"
                    // onClick={ () => dispatch(changePassword(passwordFormik.values)).unwrap()}
                    disabled={passwordLoading || !passwordFormik.isValid || !passwordFormik.dirty}
                  >
                    {t('profile.updatePassword')}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Profile; 